<script setup>
import { onMounted, ref, onBeforeUnmount } from 'vue'
import api from '@/composables/onlineSurvey/api.js'
import { Form } from 'vee-validate'
import * as Yup from 'yup'
import { useCaptcha } from '@/composables/onlineSurvey/captcha.js'
import TextInput from '@/components/onlineSurvey/FinishTypes/finishForm/TextInput.vue'
import Select from '@/components/onlineSurvey/FinishTypes/finishForm/Select.vue'
import TextareaInput from '@/components/onlineSurvey/FinishTypes/finishForm/TextareaInput.vue'
import Checkbox from '@/components/onlineSurvey/FinishTypes/finishForm/Checkbox.vue'
import CaptchaBox from '@/components/onlineSurvey/FinishTypes/finishForm/CaptchaBox.ce.vue'
import FinishMessage from './FinishMessage.vue'
import { useI18n } from '@/i18n'

const { t } = useI18n()

const props = defineProps({
  uuid: {
    type: String,
    required: true,
  },
  theme: {
    type: String,
    default: 'dark',
  },
  primaryColor: {
    type: String,
    default: '#108cc7',
  },
  message: {
    type: String,
    default: null,
  },
  baseUrl: {
    type: String,
    default: '',
    required: true,
  },
  thankYouMessage: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['formCompleted'])

//const markdown = new MarkdownIt();
const fetchingData = ref(true)
const sendingData = ref(false)
const data = ref([])
const fieldsets = ref(null)
const fieldsetCount = ref(0)
const answers = ref([])
const hasNextFieldset = ref(true)
const invalidSubmisMessage = ref('')
const participationResult = ref(null)
const primaryColor = ref('blue')
const secondaryColor = ref('red')
const complete = ref(false)
const responseMessage = ref(props.message)
const defaultThankYou = ref(
  t('defaultThankYouHtml'),
)
const protectionTimer = ref(null)
const protectionString = ref('')
const protectingForm = ref(false)
const formTouched = ref(false)

const {
  captchaNonce,
  captchaPending,
  captchaError,
  captchaChallenge,
  captchaPrefix,
  captchaSolved,
  captchaSolve,
  resetCaptcha,
} = useCaptcha()

// Using yup to generate a validation schema
// https://vee-validate.logaretm.com/v4/guide/validation#validation-schemas-with-yup
const schema = ref(null)

onMounted(() => {
  getData()
})

onBeforeUnmount(() => {
  clearInterval(protectionTimer.value)
})

async function getData() {
  fetchingData.value = true
  let response = await api.get(`${props.baseUrl}campaign/${props.uuid}`)

  if (response) {
    data.value = response.data
    primaryColor.value = response.data.company.primaryColor
    secondaryColor.value = response.data.company.secondaryColor
    fieldsets.value = response.data.form.fieldsets

    // former store
    //campaignStore.setFieldsets(response.data.form.fieldsets);

    fieldsets.value.forEach((fieldset, index) => {
      if (index === 0) {
        fieldset.inPipeline = true
        fieldset.isActive = true
      } else {
        // check, if ifAnswer is set. if not, set it to true, else set it to false
        // gibt es KEIN ifAnswer, kommt das Fieldset auf jeden Fall noch dran
        if (!fieldset.ifAnswer || fieldset.ifAnswer === undefined) {
          fieldset.inPipeline = true
        } else {
          fieldset.inPipeline = false
        }

        fieldset.isActive = false
      }
    })

    // count elements in response.data.data.form.fieldsets
    fieldsetCount.value = response.data.form.fieldsets.length
    generateYupSchema()
  }
}

function generateYupSchema() {
  let yupSchema = {}
  fieldsets.value.forEach((set) => {
    set.fields.forEach((field) => {
      if (field.isRequired === 1) {
        if (field.inputType === 'email') {
          yupSchema[field.name] = Yup.string()
            .email(t('emailInvalid'))
            .required(t('emailInvalid'))
            .label(field.label)
        } else {
          yupSchema[field.name] = Yup.string()
            .required(t('requiredField'))
            .label(field.label)
        }
      } else if (field.inputType === 'email') {
        yupSchema[field.name] = Yup.string('')
          .email(t('emailInvalidShort'))
          .label(field.label)
      }
    })
  })
  schema.value = Yup.object().shape(yupSchema)
  fetchingData.value = false
}

function onInvalidSubmit() {
  invalidSubmisMessage.value = t('checkInputs')
}

async function onSubmit(values) {
  // Send Data
  let formData = new FormData()
  Object.keys(values).forEach((key) => {
    formData.append(key, values[key])
  })

  // CAPTCHA
  const fCaptcha = captchaChallenge.value + captchaNonce.value
  formData.append('fCaptcha', fCaptcha)

  sendingData.value = true
  let response = await api.post(`${props.baseUrl}participation/campaign/${props.uuid}`, formData)
  console.log(response)
  participationResult.value = response.data
  sendingData.value = false
  complete.value = true
  emit('formCompleted', participationResult.value.identity)
}

function stateToStore(event, questionId, key) {
  addAnswer(event.target.value, questionId, key)
  checkPipelineStatus()
}

function addAnswer(value, questionId, key) {
  // check, if questionId already exists in answers
  // if yes, update the value
  // if no, add the questionId and value to answers
  let answerExists = false

  answers.value.forEach((answer, index) => {
    if (answer.questionId === questionId) {
      console.log('exists')
      answerExists = true
      answer.value = value
    }
  })

  if (!answerExists) {
    answers.value.push({
      key: key,
      questionId: questionId,
      value: value,
    })
  }
}

function checkPipelineStatus() {
  // prüfen, ob ifquestionId in den Fieldsets vorhanden ist
  // wenn ja, prüfen, ob ifAnswer in den Answers vorhanden ist
  // wenn ja, inPipeline auf true setzen
  // wenn nein, inPipeline auf false setzen
  fieldsets.value.forEach((fieldset, index) => {
    if (fieldset.ifQuestionId && fieldset.ifAnswer) {
      let ifAnswerExists = false

      console.log(answers.value)
      answers.value.forEach((answer, index) => {
        if (answer.questionId === fieldset.ifQuestionId && answer.value === fieldset.ifAnswer) {
          // if answer exists, set inPipeline to true
          ifAnswerExists = true
        }
      })

      if (ifAnswerExists) {
        fieldset.inPipeline = true
      } else {
        fieldset.inPipeline = false
      }
    }
  })
}

function checkSelectOptionsForTranslation(options, name) {
  // check, if name ist gender and translate them to given language

  if (name === 'gender') {
    // loop through options
    options.forEach((option, index) => {
      if (option === 'female') {
        if (props.lang === 'de') {
          options[index] = {
            label: 'Frau',
            value: 'female',
          }
        }
      } else if (option === 'male') {
        if (props.lang === 'de') {
          options[index] = {
            label: 'Mann',
            value: 'male',
          }
        }
      } else if (option === 'divers') {
        if (props.lang === 'de') {
          options[index] = {
            label: 'Divers',
            value: 'divers',
          }
        }
      }
    })
  } else {
    // check, if options are an array or a string
    if (typeof options === 'string') {
      options = options.split('\n')
    }
  }

  return options
}

async function protectTheForm() {
  protectingForm.value = true
  protectionString.value = Math.random().toString(36).substring(2, 16)
  await api.post(`${props.baseUrl}formprotect/${protectionString.value}`, null)
  protectingForm.value = false
}

function captcha() {
  if (captchaPending.value || captchaError.value || captchaSolved.value) {
    return
  }
  captchaSolve('form', props.baseUrl)
}
</script>

<template>
  <div id="form-component">
    <div class="container" v-if="!responseMessage">
      <div v-if="!fetchingData" class="section p-0">
       <div v-html="thankYouMessage"></div>
        <template v-if="!complete">
          <Form
            @submit="onSubmit"
            :validation-schema="schema"
            @invalid-submit="onInvalidSubmit"
            aria-label="Teilnahmeformular"
          >
            <div
              v-for="fieldset in fieldsets"
              :key="fieldset.uuid"
              :class="theme === 'dark' ? 'has-text-white' : 'has-text-black'"
            >
              <div v-if="fieldset.inPipeline" class="container">
                <div class="columns is-multiline">
                  <div
                    v-for="field in fieldset.fields"
                    :key="field.message"
                    :class="`field column is-${field.colSize} is-12-mobile`"
                  >
                    <label
                      v-if="field.fieldType !== 'checkbox'"
                      :class="`label ${theme === 'dark' ? 'has-text-white' : 'has-text-black'}`"
                    >
                      {{ field.label }}{{ field.isRequired === 1 ? '*' : '' }}
                    </label>

                    <div v-if="field.fieldType === 'text'" class="control has-icons-right">
                      <TextInput
                        :name="field.name"
                        :type="field.inputType"
                        :placeholder="field.placeholderText"
                        :label="field.label"
                        :options="[]"
                        :isRequired="field.isRequired"
                        :disabled="sendingData"
                        @change="stateToStore($event, field.id, field.name)"
                        @focus="captcha()"
                      />
                    </div>

                    <div v-else-if="field.fieldType === 'select'" class="control has-icons-right">
                      <Select
                        :name="field.name"
                        :type="field.fieldType"
                        :placeholder="field.placeholder"
                        :label="field.label"
                        :options="checkSelectOptionsForTranslation(field.inputOptions, field.name)"
                        :key="field.name"
                        :isRequired="field.isRequired"
                        :disabled="sendingData"
                        @change="stateToStore($event, field.id, field.name)"
                        @focus="captcha()"
                      />
                    </div>

                    <div v-else-if="field.fieldType === 'textarea'" class="control has-icons-right">
                      <TextareaInput
                        :name="field.name"
                        :type="field.fieldType"
                        :placeholder="field.placeholderText"
                        :label="field.label"
                        :options="[]"
                        :isRequired="field.isRequired"
                        :disabled="sendingData"
                        @change="stateToStore($event, field.id, field.name)"
                        @focus="captcha()"
                      />
                    </div>

                    <div v-else-if="field.fieldType === 'checkbox'" class="control has-icons-right">
                      <Checkbox
                        :name="field.name"
                        :type="field.inputType"
                        :label="field.label"
                        :isRequired="field.isRequired"
                        :disabled="sendingData"
                        @change="stateToStore($event, field.id, field.name)"
                        @focus="captcha()"
                      />
                    </div>

                    <template v-if="field.hintText">
                      <p class="is-size-7 is-italic my-2">👉 {{ field.hintText }}</p>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <div class="container">
              <div class="columns is-multiline">
                <div v-if="data.form.showDprText === 1" class="column is-12">
                  <p
                    :class="`is-size-7 my-2  ${
                      theme === 'dark' ? 'has-text-white' : 'has-text-black'
                    }`"
                    v-html="
                      data.form.formDprInfo
                        ? data.form.formDprInfo.html
                        : data.form.dprDefaultText.html
                    "
                  ></p>
                </div>

                <div
                  v-if="invalidSubmisMessage"
                  :class="`column is-12 has-text-danger has-text-weight-bold`"
                >
                  {{ invalidSubmisMessage }}
                </div>

                <div class="column is-12">
                  <button
                    :aria-label="t('formAriaLabel')"
                    part="submitbutton"
                    :disabled="!captchaNonce ? true : false"
                    :class="`button is-primary is-pulled-right is-medium ${
                      sendingData || captchaPending ? 'is-loading' : ''
                    }`"
                  >
                    {{ data.form.buttonText }}
                  </button>
                </div>

                <div class="column is-12">
                  <CaptchaBox :captchaPending :captchaSolved @solveCaptcha="captcha" />
                </div>

                <div
                  :class="`column is-12 is-size-7 ${
                    theme === 'dark' ? 'has-text-white' : 'has-text-black'
                  }`"
                >
                  *) {{ t('requiredLabel') }}
                </div>
              </div>
            </div>
          </Form>
        </template>
        <template v-else>
          <div
            v-if="participationResult.responseMessage"
            :class="`${theme === 'dark' ? 'has-text-white' : 'has-text-black'}`"
          >
            <div v-html="participationResult.responseMessage.html" class="content"></div>
          </div>
          <div v-else :class="`${theme === 'dark' ? 'has-text-white' : 'has-text-black'}`">
            <div v-html="defaultThankYou" class="content"></div>
          </div>
        </template>
      </div>
    </div>
    <div v-else :class="theme === 'dark' ? 'has-text-white' : 'has-text-black'">
      <FinishMessage :message="responseMessage" />
    </div>
  </div>
</template>

<style>
#freeda-component * {
  box-sizing: border-box;
}
#app-version {
  font-size: 0.7em;
  padding: 0.5em;
  color: lightgrey;
  opacity: 0.3;
}

#app-version a {
  color: lightgrey;
}

#app-env {
  position: fixed;
  top: 20px;
  right: 0;
  font-size: 1.5em;
  padding: 0.5em;
  font-weight: bold;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  width: auto;
}

.is-primary,
.is-primary:hover,
.has-background-primary {
  background-color: var(--primary-color) !important;
  color: white;
}
</style>
